import z from 'zod';

import { zodDateOrString, zodDateOrStringImplyNineteenHundreds } from '../utils/Zod';

export const zodJSPlainDate = zodDateOrString.brand<'PlainDate'>().or(zodDateOrString).meta({
  id: 'plainDateString',
  type: 'string',
  effectType: 'input',
  description:
    'A plain date string is a string parsable with new Date(<string>) in Javascript, representing a UTC date without a time component. Examples include formatted dates such as “10/1/2022” or an ISO 8601 timestamp "2022-10-01T00:00:00Z".',
});
export const zodJSInstantDate = zodDateOrString.brand<'Instant'>().or(zodDateOrString).meta({
  id: 'instantDateString',
  type: 'string',
  effectType: 'input',
  description:
    'An instant date string is a string parsable with new Date(<string>) in Javascript, representing a date with a time component. Examples include an ISO 8601 timestamp, though a formatted date such as "10/1/2022" will be accepted and interpreted as midnight UTC.',
});

/**
 * Use with caution as this field mixes instances with plain dates.
 * Defined as a union so JSPlainDate and JSInstantDate are both assignable to JSMixedDate.
 */
export const zodJSMixedDate = z.union([zodJSPlainDate, zodJSInstantDate]);

export type JSPlainDate = z.infer<typeof zodJSPlainDate>;
export type JSInstantDate = z.infer<typeof zodJSInstantDate>;
export type JSMixedDate = z.infer<typeof zodJSMixedDate>;

// could maybe assert this is UTC midnight
// might need to account for null dates that we use to clear out values
export function toJSPlainDate(date: Date): JSPlainDate;
export function toJSPlainDate(date: Date | null): JSPlainDate | null;
export function toJSPlainDate(date: Date | undefined): JSPlainDate | undefined;
export function toJSPlainDate(date: Date | undefined | null): JSPlainDate | undefined | null;
export function toJSPlainDate(date: Date | undefined | null): JSPlainDate | undefined | null {
  if (date) return date as JSPlainDate;
  return date;
}

const stripTimeUTC = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

/**
 * Convert a Date or Date-parsable string to 00:00 midnight UTC.
 * If a timestamp is provided, its Date components will be interpreted in UTC time.
 */
export function parseJSPlainDate(date: Date | string, implyTwoThousands?: boolean): JSPlainDate;
export function parseJSPlainDate(date: Date | string | null, implyTwoThousands?: boolean): JSPlainDate | null;
export function parseJSPlainDate(date: Date | string | undefined, implyTwoThousands?: boolean): JSPlainDate | undefined;
export function parseJSPlainDate(
  date: Date | string | undefined | null,
  implyTwoThousands?: boolean,
): JSPlainDate | undefined | null;

export function parseJSPlainDate(
  date: Date | string | undefined | null,
  implyTwoThousands = true,
): JSPlainDate | undefined | null {
  if (date == null) return date;

  if (typeof date === 'string') {
    date = implyTwoThousands ? zodDateOrString.parse(date) : zodDateOrStringImplyNineteenHundreds.parse(date);
  }

  return stripTimeUTC(date) as JSPlainDate;
}

export function toJSInstantDate(date: Date): JSInstantDate;
export function toJSInstantDate(date: Date | null): JSInstantDate | null;
export function toJSInstantDate(date: Date | undefined): JSInstantDate | undefined;
export function toJSInstantDate(date: Date | undefined | null): JSInstantDate | undefined | null;
export function toJSInstantDate(date: Date | undefined | null): JSInstantDate | undefined | null {
  if (date) return date as JSInstantDate;
  return date;
}

export function toJSMixedDate(date: Date): JSMixedDate;
export function toJSMixedDate(date: Date | null): JSMixedDate | null;
export function toJSMixedDate(date: Date | undefined): JSMixedDate | undefined;
export function toJSMixedDate(date: Date | undefined | null): JSMixedDate | undefined | null;
export function toJSMixedDate(date: Date | undefined | null): JSMixedDate | undefined | null {
  if (date) return date as JSMixedDate;
  return date;
}
